import { useState, useLayoutEffect, useRef } from "react";
import { STATIC_URL } from "../../constants";

/**
 * Hook for managing panel height calculation based on content
 */
export const usePanelHeight = (report: any) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number>(200);

  useLayoutEffect(() => {
    if (!report || !panelRef.current) return;

    const calculateHeight = () => {
      const panel = panelRef.current;
      if (!panel) return;

      const header = panel.querySelector(".panel-header") as HTMLElement;
      const content = panel.querySelector(".panel-content") as HTMLElement;

      if (!header || !content) return;

      // Temporarily set height to auto to measure content
      const originalHeight = panel.style.height;
      const originalMaxHeight = panel.style.maxHeight;

      panel.style.height = "auto";
      panel.style.maxHeight = "none";

      const headerHeight = header.offsetHeight;
      const contentHeight = content.scrollHeight;
      const totalHeight = headerHeight + contentHeight;

      // Set constraints
      const minHeight = 200;
      const maxHeight = window.innerHeight * 0.75; // 75vh
      const optimalHeight = Math.min(
        Math.max(totalHeight + 20, minHeight),
        maxHeight
      ); // +20 for padding

      setPanelHeight(optimalHeight);

      // Reset styles
      panel.style.height = originalHeight;
      panel.style.maxHeight = originalMaxHeight;
    };

    // Use a timeout to ensure DOM is fully rendered
    const timer = setTimeout(calculateHeight, 10);

    return () => clearTimeout(timer);
  }, [report]);

  return { panelRef, panelHeight };
};

/**
 * Hook for managing image URLs
 */
export const useImageUrls = (report: any) => {
  const getImageUrl = (imagePath: string | null): string | null => {
    if (!imagePath) return null;

    // If it's already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    // If it's a relative path, construct the full URL using Google Cloud Storage
    return `${STATIC_URL}${imagePath.replace(/^\//, "")}`;
  };

  const reportImages = report?.images?.map(getImageUrl).filter(Boolean) || [];
  const cleanupImages =
    report?.cleaned?.images?.map(getImageUrl).filter(Boolean) || [];
  const allImages = [...reportImages, ...cleanupImages];

  return { reportImages, cleanupImages, allImages, getImageUrl };
};
